import {
  audioStateChanged,
  heldReadReleased,
  isCameraHeld,
  isOperatorRejection,
  isOperatorSuccess,
  operatorGestured,
  signalRaised,
} from './operatorDeviceSlice';
import { operatorPrefChanged } from './operatorSlice';
import { fetchOperatorContext, fetchStock, findOrder, lookupCode } from './operatorThunks';
import { OPERATOR_ERROR, isRetryable } from './operatorErrors';
import { intentOf } from './operatorIdempotency';
import { FLOW_DISABLED_REASON, OPERATOR_FLOW } from './operatorConstants';
import { SCAN_SOURCE } from './operatorScanGate';
import { taskKeyOf } from './operatorTaskScope';
import { isWaitingOffList, orderElsewhereText, scannedOrderName, selectOperatorPrefs } from './operatorUtils';
import { AUDIO_STATE, playSignal, unlockAudio, vibrateSignal } from '../../services/operatorSignals';
import { RECEIVING_SIGNAL_RULES } from '../operatorReceiving/operatorReceivingSignals';
import { PUTAWAY_SIGNAL_RULES } from '../operatorPutaway/operatorPutawaySignals';
import { PICKING_SIGNAL_RULES } from '../operatorPicking/operatorPickingSignals';
import { PICKING_ROUTE_SIGNAL_RULES } from '../operatorPicking/operatorPickingRouteSignals';
import { PACKING_SIGNAL_RULES } from '../operatorPacking/operatorPackingSignals';
import { DISPATCH_SIGNAL_RULES } from '../operatorDispatch/operatorDispatchSignals';
import { KITS_SIGNAL_RULES } from '../operatorKits/operatorKitsSignals';
import { scanReceivingCode } from '../operatorReceiving/operatorReceivingThunks';
import { selectReceivingAccepting } from '../operatorReceiving/operatorReceivingSlice';
import { scanPutawayCode } from '../operatorPutaway/operatorPutawayThunks';
import { selectPutawayAccepting } from '../operatorPutaway/operatorPutawaySlice';
import { scanPickingCode } from '../operatorPicking/operatorPickingThunks';
import { selectPickingAccepting } from '../operatorPicking/operatorPickingSlice';
import { scanRouteCode } from '../operatorPicking/operatorPickingRouteThunks';
import { selectRouteAccepting } from '../operatorPicking/operatorPickingRouteSlice';
import { isRouteTask } from '../operatorPicking/operatorPickingRouteUtils';
import { scanPackingCode } from '../operatorPacking/operatorPackingThunks';
import { selectPackingAccepting } from '../operatorPacking/operatorPackingSlice';
import { scanDispatchCode } from '../operatorDispatch/operatorDispatchThunks';
import { selectDispatchAccepting } from '../operatorDispatch/operatorDispatchSlice';
import { scanKitCode } from '../operatorKits/operatorKitsThunks';
import { selectKitAccepting } from '../operatorKits/operatorKitsSlice';

/** What a scan result is, in a few words: "Ubicación Rack A-01". */
const lookupName = (result) => {
  switch (result.kind) {
    case 'slot':
      return `Ubicación ${result.name || result.code}`;
    case 'product':
      return `Producto ${result.sku}`;
    case 'receipt':
      return `Recepción ${result.code}`;
    case 'order':
      return `Orden ${scannedOrderName(result)}`;
    default:
      return `Insumo ${result.sku}`;
  }
};

const LOOKUP_SIGNAL_RULES = {
  // A lookup off screen (`meta.offScreenTask`) — Limpiar dropped it, or the operator left Consulta — says nothing.
  [lookupCode.typePrefix]: {
    success: ({ payload, meta }) => {
      if (meta.offScreenTask) return null;
      const results = payload.results || [];
      if (results.length === 0) return { tone: 'error', message: `${payload.code} no corresponde a nada en esta bodega.` };
      if (results.length > 1) return { tone: 'success', message: `${payload.code} es ${results.length} cosas.` };
      const [only] = results;
      // An order the lists leave out until its stock arrives: found, but it cannot be worked yet.
      if (only.kind === 'order' && isWaitingOffList(only)) return { tone: 'warning', message: orderElsewhereText(only) };
      return { tone: 'success', message: `${lookupName(only)}.` };
    },
    error: ({ meta }) => (meta.offScreenTask ? null : undefined),
  },
  // Consulta shows the stock, or why it could not, where the stock goes.
  [fetchStock.typePrefix]: { success: () => null, error: () => null },
  // An order for the queue it was scanned at opens, and its screen speaks; one that goes elsewhere says
  // where. Once the queue is off screen, nothing.
  [findOrder.typePrefix]: {
    success: ({ payload, meta }) =>
      meta.offScreenTask || payload.next_flow === meta.arg.flow
        ? null
        : { tone: 'warning', message: orderElsewhereText(payload) },
    error: ({ meta }) => (meta.offScreenTask ? null : undefined),
  },
};

/**
 * What the band says for each operator outcome, by `typePrefix`.
 *
 * A rule is `{success?, error?, retry?, keyed?}`. `success(action)` and
 * `error(action, fallback)` answer `{tone, message}`, `null` for silence, or
 * — `error` only — `undefined` for `fallback`, the default band. `retry`
 * names the flow of a write the band can send again when its answer was
 * lost. A `keyed` write travels in the band, `{flow, type, arg, key}`, and
 * goes again under its key from any screen; any other, the flow sends from
 * what its slice kept (see `workspaces/operator/operatorRetry`).
 */
export const OPERATOR_SIGNAL_RULES = {
  ...LOOKUP_SIGNAL_RULES,
  ...RECEIVING_SIGNAL_RULES,
  ...PUTAWAY_SIGNAL_RULES,
  ...PICKING_SIGNAL_RULES,
  ...PICKING_ROUTE_SIGNAL_RULES,
  ...PACKING_SIGNAL_RULES,
  ...DISPATCH_SIGNAL_RULES,
  ...KITS_SIGNAL_RULES,
};

const OUTCOME = /\/(fulfilled|rejected)$/;

/** The `typePrefix` of a thunk's outcome, or `null`. */
const typePrefixOf = (action) => {
  const match = OUTCOME.exec(action.type);
  return match ? action.type.slice(0, match.index) : null;
};

const ruleOf = (action) => {
  const type = typePrefixOf(action);
  return type ? OPERATOR_SIGNAL_RULES[type] : undefined;
};

/** What «Reintentar» sends for a lost answer: the keyed write itself, or the flow whose slice kept it. */
const retryOf = (action, rule) => {
  if (!rule.retry) return null;
  if (!rule.keyed) return rule.retry;
  return { flow: rule.retry, type: typePrefixOf(action), arg: intentOf(action.meta.arg), key: action.meta.requestId };
};

const defaultErrorSignal = (action, rule) => {
  const error = action.payload;
  if (!error) return null;
  if (error.kind === OPERATOR_ERROR.AMBIGUOUS || error.kind === OPERATOR_ERROR.CONFIRM) {
    return { tone: 'warning', message: error.message };
  }
  const retry = isRetryable(error) ? retryOf(action, rule) : null;
  if (error.kind === OPERATOR_ERROR.NO_RESPONSE) {
    return {
      tone: 'warning',
      message: retry
        ? 'Sin respuesta del servidor. Reintentar es seguro: no se registra dos veces.'
        : 'Sin respuesta del servidor.',
      retry,
    };
  }
  return { tone: 'error', message: error.message, retry };
};

/**
 * The band for an action, or `null`.
 *
 * @param {object} action
 */
export const signalFor = (action) => {
  const rule = ruleOf(action);
  if (!rule) return null;
  if (action.type.endsWith('/fulfilled')) return rule.success ? rule.success(action) : null;
  // A read the gate turned away — busy, or the camera seeing the same label — says nothing.
  if (action.meta?.condition) return null;
  const fallback = defaultErrorSignal(action, rule);
  const custom = rule.error ? rule.error(action, fallback) : undefined;
  return custom !== undefined ? custom : fallback;
};

const PICKING_WALK_TARGET = { scan: scanPickingCode, accepting: selectPickingAccepting };
const PICKING_ROUTE_TARGET = { scan: scanRouteCode, accepting: selectRouteAccepting };
const KIT_TARGET = { scan: scanKitCode, accepting: selectKitAccepting };

/**
 * Where a held camera read goes, by the flow on screen — in picking, by its
 * task: a route or an order's walk —, and whether that screen takes reads
 * again. Recepción's queue and the Kits station hold nothing: their read
 * opens another screen. A kit's destination does.
 */
const HELD_READ_TARGETS = {
  [OPERATOR_FLOW.STOCK]: { scan: lookupCode, accepting: (state) => !state.operator.lookup.loading },
  [OPERATOR_FLOW.RECEIVING]: { scan: scanReceivingCode, accepting: selectReceivingAccepting },
  [OPERATOR_FLOW.PUTAWAY]: { scan: scanPutawayCode, accepting: selectPutawayAccepting },
  [OPERATOR_FLOW.PICKING]: (task) => (isRouteTask(task) ? PICKING_ROUTE_TARGET : PICKING_WALK_TARGET),
  [OPERATOR_FLOW.PACKING]: { scan: scanPackingCode, accepting: selectPackingAccepting },
  [OPERATOR_FLOW.DISPATCH]: { scan: scanDispatchCode, accepting: selectDispatchAccepting },
  [OPERATOR_FLOW.KITS]: (task) => (task.key === taskKeyOf(OPERATOR_FLOW.KITS) ? null : KIT_TARGET),
};

const targetOf = (task) => {
  const entry = HELD_READ_TARGETS[task?.flow];
  return typeof entry === 'function' ? entry(task) : entry;
};

/** The held read's flow, if its screen takes reads and, for a camera read, its camera is still running. */
const heldReadTarget = (state) => {
  const { held, task, camera, sheets } = state.operatorDevice;
  const target = targetOf(task);
  if (!held || !target || sheets > 0) return null;
  if (isCameraHeld(held) && (camera.error || !selectOperatorPrefs(state).camera)) return null;
  return target.accepting(state) ? target : null;
};

/**
 * When the answer means what the workspace knows is stale: the flow was
 * switched off, or a warehouse or task is gone. The context is asked again
 * and the screens follow it.
 */
const staleContext = (action) => {
  const error = action.payload;
  if (action.type.startsWith(fetchOperatorContext.typePrefix)) return false;
  if (error?.kind === OPERATOR_ERROR.MODULE_INACTIVE && error.reason === FLOW_DISABLED_REASON) return true;
  // Consulta's stock is a side read: what it does not find, it says in place.
  if (fetchStock.rejected.match(action)) return false;
  return error?.kind === OPERATOR_ERROR.NOT_FOUND && /\/fetch\w*\/rejected$/.test(action.type);
};

/**
 * The operator workspace's reactions, kept in Redux and out of the screens.
 *
 * @param {Function} startListening
 */
export const registerOperatorListeners = (startListening) => {
  startListening({
    predicate: (action) => Boolean(ruleOf(action)),
    effect: (action, { dispatch }) => {
      const signal = signalFor(action);
      if (signal) dispatch(signalRaised(signal));
    },
  });

  startListening({
    actionCreator: signalRaised,
    effect: (action, { dispatch, getState }) => {
      const prefs = selectOperatorPrefs(getState());
      const { tone } = action.payload;
      if (prefs.sound && getState().operatorDevice.audio === AUDIO_STATE.READY && !playSignal(tone)) {
        dispatch(audioStateChanged(AUDIO_STATE.LOCKED));
      }
      if (prefs.vibration) vibrateSignal(tone);
    },
  });

  // Runs inside the tap that dispatched it: iOS only lets sound start there.
  startListening({
    actionCreator: operatorGestured,
    effect: (_, { dispatch }) => {
      dispatch(audioStateChanged(unlockAudio()));
    },
  });

  startListening({
    actionCreator: operatorPrefChanged,
    effect: (action, { dispatch }) => {
      if (action.payload.pref !== 'sound' || !action.payload.value) return;
      const state = unlockAudio();
      dispatch(audioStateChanged(state));
      if (state === AUDIO_STATE.READY) playSignal('step');
    },
  });

  startListening({
    predicate: (action) => isOperatorRejection(action) && staleContext(action),
    effect: (_, { dispatch }) => {
      dispatch(fetchOperatorContext());
    },
  });

  // Only after a success, once the screen takes reads again. A failure of what kept it busy
  // drops the held read instead (see operatorDeviceSlice); an answer of a task off screen is
  // not what it waited for.
  startListening({
    predicate: (action, state) =>
      isOperatorSuccess(action) && !action.meta?.offScreenTask && Boolean(heldReadTarget(state)),
    effect: (_, { dispatch, getState }) => {
      const target = heldReadTarget(getState());
      if (!target) return;
      const { code, alternatives, again, source = SCAN_SOURCE.CAMERA } = getState().operatorDevice.held;
      dispatch(heldReadReleased());
      dispatch(target.scan({ code, source, alternatives, ...(again ? { again } : {}) }));
    },
  });
};
